// src/components/EditModal.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Firebase
import {
    doc,
    getDocs,
    onSnapshot,
    query,
    where,
    orderBy,
    runTransaction,
    updateDoc,
    serverTimestamp,
    deleteField,
    collection,
} from "firebase/firestore";
import {
    deleteObject,
    getDownloadURL,
    ref as storageRef,
    uploadBytes,
} from "firebase/storage";
import { auth, db, storage } from "../firebase";
import { LegacyMatch } from "../services/legacyCertClient";
import { LegacyCertList } from "./LegacyCertList";

// Typer (dessa bör på sikt flyttas till en central `types.ts`)
import type {
    DocumentData,
    FirestoreError,
    QueryDocumentSnapshot,
    QuerySnapshot,
    Unsubscribe,
    UpdateData,
    WithFieldValue,
} from "firebase/firestore";

type FirestoreDate = any;
type PhotoURLMap = Record<string, string>;
type PhotoKey = "keyboard" | "screen" | "underside" | "topside";
type AuditAction = "created" | "updated" | "completed" | "reopened" | "delete_marked" | "delete_unmarked";

interface AuditEntry {
    action: AuditAction;
    by: string | null;
    at: string; // ISO
}

interface BaseItem {
    orderNumber: string;
    manufacturer: string;
    model: string;
    productType?: string | null;
    warrantyStartDate?: string | null;
    serial: string;
    serialBase?: string;
    serialVisit?: number;
    serialBaseKey?: string;
    chargerIncluded: boolean;
    adapterYesNo?: "Yes" | "No" | "";
    damageNotes: string;
    photos: PhotoURLMap;
    customer?: string;
    customerId?: string | null;
    articleNumber?: string;
    createdAt: FirestoreDate;
    createdBy: string | null;
    auditLog: AuditEntry[];
    completed: boolean;
    completedAt: FirestoreDate;
    completedBy: string | null;
    updatedAt?: FirestoreDate;
    lockedBy?: string | null;
    lockedAt?: string | null;
    productTypeId?: string | null;
    reuse?: boolean;
    resold?: boolean;
    scrap?: boolean;
    grade?: "A" | "B" | "C" | "D" | "E" | "";
    markedForInvoice?: boolean;
    invoiceReportId?: string | null;
    invoicedAt?: string | null;

    // Unikt AssetID för raderingscertifikat/Blancco-koppling
    assetId?: number;
    blanccoReportUuid?: string | null;

    // Vidareförsäljning
    salesBatchId?: string | null;
    salesBatchName?: string | null;
    salesKolliId?: string | null;
    salesKolliName?: string | null;
    salesCpu?: string | null;
    salesStorage?: string | null;
    salesRam?: string | null;

    deletePending?: boolean;
    deleteMarkedBy?: string | null;
    deleteMarkedAt?: string | null;
}

export interface EditFormState extends Omit<BaseItem, "photos"> {
    id: string;
    photos: PhotoURLMap;
}

// Helpers
import { validateCompletionChoice as impactValidateCompletionChoice } from "../lib/impact";
import { toKey, toMillis, PHOTO_LABELS, LOCK_STALE_MS, normalizeSerialKey, splitSerialParts, reallocateSerialOnEdit, assertValidProductTypeOrExplain, buildDisplaySerial } from "./editModalFunctions";
import { stableSaveOrchestrated } from "./stableSaveOrchestrator";
import { resolveProductTypeSelection, type ProductTypeOption } from "../lib/productTypeSelection";


export interface EditModalProps {
    isOpen: boolean;
    onClose: () => void;
    manufacturerList: string[];
    productTypeOptions: ProductTypeOption[];
    editForm: EditFormState;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    onSave: () => Promise<void> | void;

    largeImage: string | null;
    setLargeImage: React.Dispatch<React.SetStateAction<string | null>>;

    onComplete: () => Promise<void>;
    isCompleted: boolean;
    onReopen?: () => void;
    onFetchCertificate?: () => Promise<
        | void
        | {
              legacyMatches?: {
                  uuid: string;
                  reportDate?: string | null;
                  signedUrl?: string | null;
                  provider?: string;
                  bucketPath?: string | null;
                  serialNormalized?: string | null;
              }[];
          }
        | {
              blanccoHits: number;
              certusHits: number;
              legacyHits: number;
              matches: any[];
          }
    >;

    isReadOnly?: boolean;
    invoiceReportId?: string | null;
    isSaving?: boolean;

    isCustomerAccount?: boolean;

    onUnmarkDelete?: () => Promise<void>;

    setEditForm: React.Dispatch<React.SetStateAction<EditFormState>>;
    itemId: string | null;

    // Ref-objekt för att dela state mellan App och Modal
    editOriginalRef: React.MutableRefObject<{ serial: string; serialBaseKey: string | null; itemId: string | null; }>;
    lastSerialAllocRef: React.MutableRefObject<{ changed: boolean; serialBaseKey: string; serialVisit: number; displaySerial: string; } | null>;

    // signal från App: delete-flaggan togs bort medan modalen var öppen (läs-läge bibehålls)
    deleteClearedWhileOpen?: boolean;

    // Nytt för orkestrerad save
    setIsSaving: (v: boolean) => void;
    refreshHomeList: () => Promise<void>;
    setIsEditOpen: (v: boolean) => void;
    setEditId: (v: string | null) => void;
    currentUserString: () => string | null;
}

const DIRTY_KEYS: (keyof EditFormState)[] = [
    "manufacturer", "model", "productType", "productTypeId", "serial", "orderNumber",
    "chargerIncluded", "damageNotes", "reuse", "resold", "scrap",
    "grade", "warrantyStartDate", "adapterYesNo",
    "salesBatchId", "salesKolliId", "salesCpu", "salesStorage", "salesRam"
];

function EditModal(props: EditModalProps) {
    if (!props.isOpen) return null;
    return <EditModalBody {...props} />;
}

function EditModalBody({
    isOpen,
    onClose,
    manufacturerList,
    productTypeOptions,
    editForm,
    onChange,
    onSave,
    largeImage,
    setLargeImage,
    onComplete,
    isCompleted,
    onReopen,
    onFetchCertificate,
    isReadOnly = false,
    invoiceReportId = null,
    isSaving = false,
    isCustomerAccount = false,
    onUnmarkDelete,
    setEditForm,
    itemId,
    editOriginalRef,
    lastSerialAllocRef,
    deleteClearedWhileOpen,
    setIsSaving,
    refreshHomeList,
    setIsEditOpen,
    setEditId,
    currentUserString,
}: EditModalProps) {


    const [serialIndexMeta, setSerialIndexMeta] =
        React.useState<{ visits: number; active: number; lastVisit: number } | null>(null);
    const [isFetchingCert, setIsFetchingCert] = useState(false);
    const [legacyMatches, setLegacyMatches] = useState<LegacyMatch[]>([]);
    const saveInFlightRef = useRef(false);

    // Vidareförsäljning: batch/kolli
    const [salesBatchOpts, setSalesBatchOpts] = useState<Array<{ id: string; name: string }>>([]);
    const [salesKolliOpts, setSalesKolliOpts] = useState<Array<{ id: string; name: string }>>([]);

    useEffect(() => {
        if (!isOpen || isCustomerAccount) return;
        const q = query(collection(db, "salesBatches"), orderBy("createdAt", "desc"));
        const unsub = onSnapshot(q, (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, name: String((d.data() as any)?.name || "") }));
            setSalesBatchOpts(rows);
        });
        return () => unsub();
    }, [isCustomerAccount, isOpen]);

    useEffect(() => {
        if (!isOpen || isCustomerAccount) return;
        const batchId = (editForm.salesBatchId || "").trim();
        if (!batchId) { setSalesKolliOpts([]); return; }
        const q = query(collection(db, "salesBatches", batchId, "kollin"), orderBy("createdAt", "asc"));
        const unsub = onSnapshot(q, (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, name: String((d.data() as any)?.name || "") }));
            setSalesKolliOpts(rows);
        });
        return () => unsub();
    }, [editForm.salesBatchId, isCustomerAccount, isOpen]);


    // Validera innan "Markera som färdig"
    const handleCompleteValidated = async () => {
        if (isDirty) {
            alert("Spara eller återställ ändringarna innan enheten markeras som färdig.");
            return;
        }

        // 1) Befintlig validering av val (grade/status/reuse/resold/scrap)
        const chk = impactValidateCompletionChoice({
            grade: editForm.grade,
            status: (editForm as any).status,
            reuse: editForm.reuse,
            resold: editForm.resold,
            scrap: editForm.scrap,
        });
        if (!chk.ok) {
            if ("error" in chk) alert(chk.error);
            return;
        }

        if (editForm.resold) {
            const missing =
                !editForm.salesBatchId ||
                !editForm.salesKolliId ||
                !editForm.salesCpu?.trim() ||
                !editForm.salesStorage?.trim() ||
                !editForm.salesRam?.trim();
            if (missing) {
                alert("För vidaresålt krävs försäljningsbatch, kolli och ifyllda fält för CPU, Lagring och Minne.");
                return;
            }
        }

        // 2) Datakvalitet (P0d): kräver giltig produkttyp
        const okPT = await assertValidProductTypeOrExplain(
            (editForm as any).productTypeId,
            (editForm as any).productType
        );
        if (!okPT) return;

        // 3) Kör originalets complete
        await onComplete();
    };

    const [imgOk, setImgOk] = React.useState<Record<PhotoKey, boolean>>({
        keyboard: true,
        screen: true,
        underside: true,
        topside: true,
    });

    const [isDirty, setIsDirty] = useState(false);
    const [pendingPhotoFiles, setPendingPhotoFiles] = useState<Record<string, File>>({});
    const pendingPhotoFilesRef = useRef<Record<string, File>>({});
    const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
    const [pendingDeletes, setPendingDeletes] = useState<Record<string, true>>({});
    const pendingDeletesRef = useRef<Record<string, true>>({});

    const setPendingPhotoFilesSynced = React.useCallback((action: React.SetStateAction<Record<string, File>>) => {
        const next = typeof action === "function" ? action(pendingPhotoFilesRef.current) : action;
        pendingPhotoFilesRef.current = next;
        setPendingPhotoFiles(next);
    }, []);

    const setPendingDeletesSynced = React.useCallback((action: React.SetStateAction<Record<string, true>>) => {
        const next = typeof action === "function" ? action(pendingDeletesRef.current) : action;
        pendingDeletesRef.current = next;
        setPendingDeletes(next);
    }, []);

    const DIRTY_KEYS = React.useMemo((): (keyof EditFormState)[] => [
        "manufacturer", "model", "productType", "productTypeId", "serial", "orderNumber",
        "chargerIncluded", "damageNotes", "reuse", "resold", "scrap",
        "grade", "warrantyStartDate", "adapterYesNo",
        // Vidareförsäljning
        "salesBatchId", "salesBatchName", "salesKolliId", "salesKolliName",
        "salesCpu", "salesStorage", "salesRam",
    ], []);

    const baselineRef = React.useRef<{ form: Partial<EditFormState>; photos: Record<string, string> }>({
        form: DIRTY_KEYS.reduce<Partial<EditFormState>>((base, key) => {
            (base as any)[key] = (editForm as any)?.[key];
            return base;
        }, {}),
        // EditModalBody monteras om för varje öppning. Initiera därför fotobaslinjen
        // redan under första renderingen, innan den passiva effekten hinner köras.
        // Det förhindrar att ett snabbt klick på "Ta bort" blir en tyst no-op.
        photos: { ...(editForm?.photos || {}) },
    });

    React.useEffect(() => {
        if (!isOpen) return;
        const baseForm: Partial<EditFormState> = {};
        for (const k of DIRTY_KEYS) (baseForm as any)[k] = (editForm as any)?.[k];
        baselineRef.current = {
            form: baseForm,
            photos: { ...(editForm?.photos || {}) },
        };
        setPreviewUrls({});
        setPendingPhotoFilesSynced({});
        setPendingDeletesSynced({});
        setIsDirty(false);

        setImgOk({ keyboard: true, screen: true, underside: true, topside: true });

    // Note: intentionally excluding editForm from deps so baseline stays fixed for dirty-checking during an edit session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, itemId, DIRTY_KEYS, editForm.updatedAt, setPendingPhotoFilesSynced, setPendingDeletesSynced]);

    React.useEffect(() => {
        if (!isOpen) {
            editOriginalRef.current = { serial: "", serialBaseKey: null, itemId: null };
        }
    }, [isOpen, editOriginalRef]);


    React.useEffect(() => {
        const baseKey =
            (editForm as any)?.serialBaseKey
            ?? (editForm as any)?.serialBase
            ?? normalizeSerialKey(splitSerialParts(String(editForm?.serial || "")).base);

        if (!baseKey) { setSerialIndexMeta(null); return; }

        const ref = doc(db, "serialIndex", baseKey);
        const unsub = onSnapshot(ref, (snap) => {
            const x: any = snap.data() || {};
            setSerialIndexMeta({
                visits: Number(x?.visits ?? 0),
                active: Number(x?.active ?? 0),
                lastVisit: Number(x?.lastVisit ?? 0),
            });
        });

        return () => unsub();
    }, [editForm]);



    // Legacy save-steg (serial reallocation + fotouppdatering) är ersatt av stableSaveOrchestrated.
    // Lämnas kvar kommenterat för ev. felsökning/rollback.
    /*
    async function commitStagedPhotos() { ... }
    async function ensureSerialIndexReallocationIfChanged(): Promise<void> { ... }
    async function onSaveWithSerialIndex() { ... }
    */

    async function onSaveOrchestrated() {
        if (fieldsDisabled || isSaving || saveInFlightRef.current) return;
        if (!itemId) return;

        saveInFlightRef.current = true;
        try {
            await stableSaveOrchestrated({
                db,
                storage,
                editId: itemId,
                editForm,
                editIsReadOnly: !!isReadOnly,
                currentUserString,
                toMillis,
                LOCK_STALE_MS,
                splitSerialParts,
                normalizeSerialKey,
                buildDisplaySerial,
                originalSerial: editOriginalRef.current?.serial,
                originalSerialBaseKey: editOriginalRef.current?.serialBaseKey,
                lastSerialAllocRef,
                pendingPhotoFiles: pendingPhotoFilesRef.current,
                pendingDeletes: pendingDeletesRef.current,
                previewUrls,
                setPreviewUrls,
                setPendingPhotoFiles: setPendingPhotoFilesSynced,
                setPendingDeletes: setPendingDeletesSynced,
                setEditForm,
                setIsSaving,
                refreshHomeList,
                setIsEditOpen,
                setEditId,
                alertFn: (m) => alert(m),
                consoleError: (e) => console.error(e),
                editOriginalRef,
            });
        } catch (err) {
            console.error("[EditModal] orchestrated save failed", err);
            alert("Kunde inte spara ändringar: " + (err as any)?.message || err);
        } finally {
            saveInFlightRef.current = false;
        }
    }

    const setGrade = (letter: 'A' | 'B' | 'C' | 'D' | 'E', checked: boolean) => {
        if (fieldsDisabled) return;

        setEditForm(prev => {
            const next = { ...prev } as EditFormState;

            if (letter === 'E') {
                if (checked) {
                    next.grade = 'E';
                    next.scrap = true;
                    next.reuse = false;
                    next.resold = false;
                } else {
                    next.grade = '';
                    next.scrap = false;
                }
                return next;
            }

            next.grade = checked ? letter : '';
            if (next.scrap) next.scrap = false;

            return next;
        });

        setIsDirty(true);
    };

    const setCheckbox = (name: 'reuse' | 'resold' | 'scrap', val: boolean) => {
        if (fieldsDisabled) return;

        setEditForm(prev => {
            const next = { ...prev } as EditFormState;

            next.reuse = false;
            next.resold = false;
            next.scrap = false;

            (next as any)[name] = val;

            if (next.scrap) {
                next.grade = 'E';
            } else if (next.grade === 'E') {
                next.grade = '';
            }

            if (name !== 'resold' || !val) {
                next.salesBatchId = "";
                next.salesBatchName = "";
                next.salesKolliId = "";
                next.salesKolliName = "";
                next.salesCpu = "";
                next.salesStorage = "";
                next.salesRam = "";
            }

            return next;
        });

        setIsDirty(true);
    };

    const handleSalesBatchChange = (id: string) => {
        const opt = salesBatchOpts.find((o) => o.id === id);
        setEditForm((prev) => ({
            ...prev,
            salesBatchId: id,
            salesBatchName: opt?.name || "",
            salesKolliId: "",
            salesKolliName: "",
        }));
        setIsDirty(true);
    };

    const handleSalesKolliChange = (id: string) => {
        const opt = salesKolliOpts.find((o) => o.id === id);
        setEditForm((prev) => ({
            ...prev,
            salesKolliId: id,
            salesKolliName: opt?.name || "",
        }));
        setIsDirty(true);
    };

    const handleResaleField = (key: "salesCpu" | "salesStorage" | "salesRam", value: string) => {
        setEditForm((prev) => ({ ...prev, [key]: value }));
        setIsDirty(true);
    };


    const isDeletePending = !!editForm.deletePending;
    const fieldsDisabled = isReadOnly || isCompleted || isDeletePending;
    const readOnlyStyle: React.CSSProperties = fieldsDisabled
        ? { background: "#f9fafb", cursor: "not-allowed" }
        : {};
    const exactlyOneSelected =
        Number(!!editForm.reuse) + Number(!!editForm.resold) + Number(!!editForm.scrap) === 1;

    const formComplete = Boolean(
        editForm.manufacturer &&
        editForm.model &&
        String(editForm.serial || "").trim() &&
        exactlyOneSelected &&
        !!editForm.grade
    );

    const [reportPreview, setReportPreview] = useState<any | null>(null);
    const [reportError, setReportError] = useState<string | null>(null);
    const [reportLoading, setReportLoading] = useState(false);
    const [showLogModal, setShowLogModal] = useState(false);

    const [editUploadPct, setEditUploadPct] = useState<Record<string, number>>({});

    async function handleEditPhotoFile(photoType: string, file: File) {
        if (fieldsDisabled) return;

        try {
            const prev = previewUrls[photoType];
            if (prev && prev.startsWith("blob:")) {
                try { URL.revokeObjectURL(prev); } catch { /* ignorera, städning är bäst-effort */ }
            }

            const objectUrl = URL.createObjectURL(file);
            const nextPreview = { ...previewUrls, [photoType]: objectUrl };
            const nextPendingFiles = { ...pendingPhotoFilesRef.current, [photoType]: file };

            const nextPendingDeletes = { ...pendingDeletesRef.current };
            delete nextPendingDeletes[photoType];

            setPreviewUrls(nextPreview);
            setPendingPhotoFilesSynced(nextPendingFiles);
            setPendingDeletesSynced(nextPendingDeletes);

            setImgOk((s) => ({ ...s, [photoType as PhotoKey]: true }));

            setEditUploadPct((prev) => ({ ...prev, [photoType]: 0 }));

        } catch (e: any) {
            alert(e?.message || "Kunde inte förhandsvisa bilden.");
        }
    }

    function handleEditPhotoChange(
        e: React.ChangeEvent<HTMLInputElement>,
        key: PhotoKey
    ) {
        const file = e.target.files?.[0];
        if (!file || fieldsDisabled) return;
        void handleEditPhotoFile(key, file);
        e.currentTarget.value = "";
    }

    function handleEditRemovePhoto(key: PhotoKey) {
        if (fieldsDisabled) return;

        setPendingPhotoFilesSynced(prev => {
            const next = { ...prev }; delete next[key]; return next;
        });

        setPendingDeletesSynced(prev => ({ ...prev, [key]: true }));

        setPreviewUrls(prev => {
            const next = { ...prev };
            const u = next[key];
            if (u?.startsWith?.("blob:")) { try { URL.revokeObjectURL(u); } catch { /* ignorera, städning är bäst-effort */ } }
            delete next[key];
            return next;
        });

        setImgOk((s) => ({ ...s, [key]: true }));

        setEditForm(prev => {
            const photos = { ...(prev.photos || {}) } as Record<string, string>;
            delete photos[key];
            return { ...prev, photos };
        });

        setImgOk(s => ({ ...s, [key]: true }));
    }

    const [modelOptions, setModelOptions] = React.useState<string[]>([]);

    const computeNetDirty = React.useCallback(() => {
        for (const k of DIRTY_KEYS) {
            const baseVal = (baselineRef.current.form as any)?.[k];
            const curVal = (editForm as any)?.[k];
            if (baseVal !== curVal) return true;
        }

        if (Object.keys(pendingPhotoFiles).length > 0) return true;

        for (const t of Object.keys(pendingDeletes)) {
            if (baselineRef.current?.photos?.[t]) return true;
        }

        return false;
    }, [editForm, pendingPhotoFiles, pendingDeletes, DIRTY_KEYS]);

    React.useEffect(() => {
        setIsDirty(computeNetDirty());
    }, [computeNetDirty]);

    React.useEffect(() => {
        return () => {
            try {
                Object.values(previewUrls).forEach((u) => {
                    if (typeof u === "string" && u.startsWith("blob:")) {
                        URL.revokeObjectURL(u);
                    }
                });
            } catch { /* ignorera, städning är bäst-effort */ }
        };
    }, [isOpen, previewUrls]);


    const getPhotoSrcForUI = (type: string): string | undefined => {
        if (previewUrls[type]) return previewUrls[type];
        if (pendingDeletes[type]) return undefined;
        const url = (editForm?.photos as any)?.[type] as string | undefined;
        return url;
    };

    const photoInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({});

    const triggerPhotoCapture = (type: string) => {
        if (fieldsDisabled) return;
        const el = photoInputRefs.current[type];
        if (el) el.click();
    };

    function restorePhotoForType(type: string) {
        if (fieldsDisabled) return;

        const preview = previewUrls[type];
        if (preview?.startsWith?.("blob:")) {
            try { URL.revokeObjectURL(preview); } catch { /* best-effort */ }
        }

        setPreviewUrls((prev) => {
            const next = { ...prev };
            delete next[type];
            return next;
        });
        setPendingPhotoFilesSynced((prev) => {
            const next = { ...prev };
            delete next[type];
            return next;
        });
        setPendingDeletesSynced((prev) => {
            const next = { ...prev };
            delete next[type];
            return next;
        });
        setImgOk((prev) => ({ ...prev, [type as PhotoKey]: true }));
    }

    async function removePhotoForType(type: string) {
        if (fieldsDisabled) return;

        // Kryss på en staged ny/ersättningsbild betyder ångra staging och gå
        // tillbaka till den sparade slotten, inte radera originalbilden.
        if (pendingPhotoFilesRef.current[type]) {
            restorePhotoForType(type);
            return;
        }

        const prev = previewUrls[type];
        if (prev && prev.startsWith("blob:")) {
            try { URL.revokeObjectURL(prev); } catch { /* ignorera, inte kritiskt om modell-laddning felar */ }
        }

        const hadBaseline = !!baselineRef.current?.photos?.[type]
            || !!(editForm?.photos as Record<string, string> | undefined)?.[type];

        const nextPreview = { ...previewUrls }; delete nextPreview[type];
        const nextPendingFiles = { ...pendingPhotoFilesRef.current }; delete nextPendingFiles[type];

        const nextPendingDeletes: Record<string, true> = { ...pendingDeletesRef.current };
        if (hadBaseline) {
            nextPendingDeletes[type] = true;
        } else {
            delete nextPendingDeletes[type];
        }

        setPreviewUrls(nextPreview);
        setPendingPhotoFilesSynced(nextPendingFiles);
        setPendingDeletesSynced(nextPendingDeletes);
    }


    React.useEffect(() => {
        if (!isOpen) return;

        const loadModelsForManufacturer = async () => {
            if (!editForm.manufacturer) { setModelOptions([]); return; }

            const slug = toKey(editForm.manufacturer);

            let snap = await getDocs(
                query(collection(db, "models"), where("manufacturerKey", "==", slug))
            );

            if (snap.empty) {
                snap = await getDocs(
                    query(collection(db, "models"), where("manufacturer", "==", editForm.manufacturer))
                );
            }

            const options = snap.docs
                .map(d => ((d.data() as any).name as string) || "")
                .filter(Boolean)
                .filter((v, i, a) => a.findIndex(x => x.toLowerCase() === v.toLowerCase()) === i)
                .sort((a, b) => a.localeCompare(b, "sv"));

            setModelOptions(options);
            if (editForm.model && !options.includes(editForm.model)) {
                onChange({
                    target: { name: "model", value: "", type: "select-one" }
                } as React.ChangeEvent<HTMLSelectElement>);
            }
        };

        loadModelsForManufacturer();
    }, [isOpen, editForm.manufacturer, editForm.model, onChange]);


    const sortedLog = [...(editForm.auditLog || [])].sort(
        (a, b) => +new Date(b.at) - +new Date(a.at)
    );
    const limitedLog = sortedLog.slice(0, 3);

    const labelForAction = (action: string): string => {
        if (action === "created") return "Skapad av";
        if (action === "updated") return "Ändrat av";
        if (action === "completed") return "Markerad som färdig av";
        return action;
    };

    const requestClose = React.useCallback(() => {
        if (isDirty) {
            const ok = window.confirm("Du har osparade ändringar. Vill du kasta dem?");
            if (!ok) return;
            Object.values(previewUrls).forEach((u) => { if (u?.startsWith?.("blob:")) { try { URL.revokeObjectURL(u); } catch { /* ignorera */ } } });
            setPreviewUrls({});
            setPendingPhotoFilesSynced({});
            setPendingDeletesSynced({});
            setIsDirty(false);
        }
        onClose();
    }, [isDirty, previewUrls, onClose, setPendingPhotoFilesSynced, setPendingDeletesSynced]);

    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!isDirty) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [isDirty]);

    return (
        <div
            className="gw-modal-overlay"
            onClick={requestClose}
            role="dialog"
            aria-modal="true"
        >
            <div
                className="gw-modal-card gw-modal-card--wide"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="gw-modal-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <h3 className="gw-modal-title">Redigera enhet</h3>
                        {isCompleted && <span className="badge badge--done">Färdig</span>}
                        {isDeletePending && (
                            <span className="badge badge--danger" title={editForm.deleteMarkedBy || undefined}>
                                Markerad för radering{editForm.deleteMarkedBy ? ` av ${editForm.deleteMarkedBy}` : ""}
                            </span>
                        )}
                    </div>
                    {(itemId || editForm.assetId !== undefined) && (
                        <span className="text-muted" style={{ fontSize: 12, display: "flex", gap: 12, alignItems: "center" }}>
                            {editForm.assetId !== undefined && (
                                <span title="AssetID (koppling mot Blancco)">
                                    AssetID: {editForm.assetId}
                                </span>
                            )}
                            {itemId && (
                                <span>
                                    ID: {itemId}
                                </span>
                            )}
                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    type="button"
                                    className="gw-btn gw-btn-secondary gw-btn-xs"
                                    disabled={
                                        !onFetchCertificate ||
                                        isFetchingCert ||
                                        (editForm.assetId === undefined && !(editForm as any).blanccoReportUuid)
                                    }
                                    onClick={async () => {
                                        if (!onFetchCertificate) return;
                                        try {
                                            setIsFetchingCert(true);
                                            const res = await onFetchCertificate();
                                            if (res && "legacyMatches" in res && Array.isArray(res.legacyMatches)) {
                                                setLegacyMatches(res.legacyMatches);
                                            } else {
                                                setLegacyMatches([]);
                                            }
                                        } catch (err: any) {
                                            alert(err?.message || "Kunde inte hämta certifikat.");
                                        } finally {
                                            setIsFetchingCert(false);
                                        }
                                    }}
                                    title={
                                        (editForm.assetId === undefined && !(editForm as any).blanccoReportUuid)
                                            ? "Saknar assetId/UUID för Blancco-sökning"
                                            : undefined
                                    }
                                >
                                    {isFetchingCert ? "Hämtar…" : "Certifikat"}
                                </button>
                                <button
                                    type="button"
                                    className="gw-btn gw-btn-secondary gw-btn-xs"
                                    onClick={() => setShowLogModal(true)}
                                    title="Visa full logg"
                                >
                                    Logg
                                </button>
                            </div>
                        </span>
                    )}
                </div>

            <div className="gw-modal-body">
                {/* legacyMatches list visas i separat modal */}

                {showLogModal && createPortal(
                    <div
                        className="gw-modal-overlay"
                        onClick={() => setShowLogModal(false)}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div
                            className="gw-modal-card"
                            style={{ maxWidth: "520px", width: "90vw", maxHeight: "80vh", display: "flex", flexDirection: "column" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="gw-modal-header" style={{ position: "sticky", top: 0, zIndex: 2 }}>
                                <div className="gw-modal-title">Logg</div>
                                <button type="button" className="gw-btn gw-btn-ghost gw-btn-xs" onClick={() => setShowLogModal(false)}>
                                    Stäng
                                </button>
                            </div>
                            <div className="flex-1 min-h-0 overflow-y-auto" style={{ padding: "12px 16px" }}>
                                {sortedLog.length === 0 ? (
                                    <div style={{ color: "#6b7280" }}>Ingen historik ännu.</div>
                                ) : (
                                    (sortedLog as AuditEntry[]).map((entry, idx) => (
                                        <div key={idx} style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                {new Date(entry.at).toLocaleString("sv-SE")}
                                            </div>
                                            <div>
                                                <strong>{labelForAction(entry.action as string)}</strong>{" "}
                                                {entry.by || "—"}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {isCustomerAccount ? (
                    <div className="gw-banner gw-banner--info" role="status" aria-live="polite">
                        Läs-läge (kundkonto). Du kan se historik och bilder.
                    </div>
                ) : invoiceReportId ? (
                        <div className="gw-banner gw-banner--warn" role="status" aria-live="polite">
                            <div style={{ marginBottom: 8 }}>
                                <strong>Denna enhet är fakturerad.</strong> Fälten är låsta men du kan se historik och bilder.
                            </div>
                            <a
                                href={`#/rapport/${encodeURIComponent(invoiceReportId)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="btn"
                                title="Öppna fakturarapport"
                            >
                                Öppna fakturarapport →
                            </a>
                        </div>
    ) : isReadOnly ? (
        <div className="gw-banner gw-banner--warn" role="status" aria-live="polite">
            Posten är låst för redigering.
        </div>
    ) : null}

    {deleteClearedWhileOpen && (
        <div className="gw-banner gw-banner--info" role="status" aria-live="polite">
            Raderingsflaggan togs bort. Stäng och öppna igen för att börja redigera.
        </div>
    )}

    {isDeletePending && (
        <div
            className="gw-banner gw-banner--danger"
            role="alert"
            aria-live="assertive"
                        >
                            <div style={{ marginBottom: 8 }}>
                                <strong>Denna enhet är markerad för radering.</strong> Alla fält är låsta tills raderingen
                                avmarkeras i listan på startsidan.
                            </div>
                            <div style={{ fontSize: 13, opacity: 0.9 }}>
                                Markerad av {editForm.deleteMarkedBy || "okänd"}
                                {editForm.deleteMarkedAt ? ` • ${new Date(editForm.deleteMarkedAt).toLocaleString()}` : ""}
                            </div>
                        </div>
                    )}

                    <div className="gw-form-grid">
                        <label className="gw-form-field">
                            <div className="gw-form-label">Tillverkare</div>
                            <select
                                name="manufacturer"
                                value={editForm.manufacturer || ""}
                                onChange={(e) => {
                                    onChange(e);
                                    onChange({
                                        target: { name: "model", value: "" },
                                    } as unknown as React.ChangeEvent<HTMLSelectElement>);
                                }}
                                disabled={fieldsDisabled}
                                className="gw-input"
                            >
                                <option value="">Välj tillverkare</option>
                                {manufacturerList.map((m) => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </select>
                        </label>

                        <label className="gw-form-field">
                            <div className="gw-form-label">Modell</div>
                            <select
                                name="model"
                                value={editForm.model || ""}
                                onChange={onChange}
                                disabled={!editForm.manufacturer || fieldsDisabled}
                                className="gw-input"
                            >
                                <option value="">{editForm.manufacturer ? "Välj modell" : "Välj tillverkare först"}</option>
                                {modelOptions.map((m) => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </select>
                        </label>

                        <label className="gw-form-field">
                            <div className="gw-form-label">Produkttyp</div>
                            <select
                                name="productTypeId"
                                value={editForm.productTypeId || ""}
                                onChange={(e) => {
                                    const selection = resolveProductTypeSelection(productTypeOptions, e.target.value);
                                    setEditForm((prev) => ({ ...prev, ...selection }));
                                }}
                                disabled={fieldsDisabled}
                                className="gw-input"
                            >
                                <option value="">Välj produkttyp</option>
                                {!!editForm.productTypeId &&
                                    !productTypeOptions.some((option) => option.id === editForm.productTypeId) && (
                                        <option value={editForm.productTypeId} disabled>
                                            {editForm.productType || editForm.productTypeId} (saknas eller är inaktiv)
                                        </option>
                                    )}
                                {productTypeOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.label} — {option.id}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="gw-form-field">
                            <div className="gw-form-label">Ordernummer</div>
                            <input
                                type="text"
                                name="orderNumber"
                                value={editForm.orderNumber || ""}
                                onChange={onChange}
                                disabled={fieldsDisabled}
                                className="gw-input"
                                placeholder="Ordernummer"
                            />
                        </label>

                        <label className="gw-form-field">
                            <div className="gw-form-label">
                                Serienummer
                                {serialIndexMeta && (
                                    <span
                                        title={`Index: visits=${serialIndexMeta.visits}, active=${serialIndexMeta.active}, lastVisit=${serialIndexMeta.lastVisit}`}
                                        style={{ marginLeft: 6, cursor: "help", opacity: 0.8 }}
                                    >
                                        ⓘ
                                    </span>
                                )}
                            </div>
                            <input
                                type="text"
                                name="serial"
                                value={editForm.serial || ""}
                                onChange={onChange}
                                disabled={fieldsDisabled}
                                className="gw-input"
                                placeholder="Serienummer"
                            />
                        </label>

                        <label className="gw-form-field gw-form-row-full">
                            <div className="gw-check-inline">
                                <input
                                    type="checkbox"
                                    name="chargerIncluded"
                                    checked={!!editForm.chargerIncluded}
                                    onChange={onChange}
                                    disabled={fieldsDisabled}
                                />
                                <span>Adapter medföljer</span>
                            </div>
                        </label>


                        <label className="gw-form-field gw-form-row-full">
                            <div className="gw-form-label">Eventuella skador</div>
                            <textarea
                                name="damageNotes"
                                value={editForm.damageNotes || ""}
                                onChange={onChange}
                                disabled={fieldsDisabled}
                                className="gw-input"
                                placeholder="Beskriv skador…"
                                style={{ minHeight: 88 }}
                            />
                        </label>
                    </div>

                    <div className="gw-form-row-compact">
                        <fieldset className="gw-fieldset gw-form-field" style={{ flex: "0 1 50%" }}>
                            <legend className="gw-fieldset-legend">Status</legend>
                            <div className="gw-inline-checks">
                                <label className="gw-check-inline">
                                    <input
                                        type="checkbox"
                                        name="reuse"
                                        checked={!!editForm.reuse}
                                        onChange={(e) => setCheckbox('reuse', (e.target as HTMLInputElement).checked)}
                                        disabled={fieldsDisabled}
                                    />
                                    <span>Återbruk</span>
                                </label>

                                <label className="gw-check-inline">
                                    <input
                                        type="checkbox"
                                        name="resold"
                                        checked={!!editForm.resold}
                                        onChange={(e) => setCheckbox('resold', (e.target as HTMLInputElement).checked)}
                                        disabled={fieldsDisabled}
                                    />
                                    <span>Vidaresålt</span>
                                </label>

                                <label className="gw-check-inline">
                                    <input
                                        type="checkbox"
                                        name="scrap"
                                        checked={!!editForm.scrap}
                                        onChange={(e) => setCheckbox('scrap', (e.target as HTMLInputElement).checked)}
                                        disabled={fieldsDisabled}
                                    />
                                    <span>Skrotad</span>
                                </label>
                            </div>
                        </fieldset>

                        <fieldset className="gw-fieldset gw-form-field" style={{ flex: "0 1 50%" }}>
                            <legend className="gw-fieldset-legend">Gradering</legend>
                            <div className="gw-inline-checks">
                                <label className="gw-check-inline">
                                    <input
                                        type="checkbox"
                                        checked={editForm.grade === 'A'}
                                        onChange={(e) => setGrade('A', (e.target as HTMLInputElement).checked)}
                                        disabled={fieldsDisabled || !!editForm.scrap}
                                    />
                                    <span>A</span>
                                </label>
                                <label className="gw-check-inline">
                                    <input
                                        type="checkbox"
                                        checked={editForm.grade === 'B'}
                                        onChange={(e) => setGrade('B', (e.target as HTMLInputElement).checked)}
                                        disabled={fieldsDisabled || !!editForm.scrap}
                                    />
                                    <span>B</span>
                                </label>
                                <label className="gw-check-inline">
                                    <input
                                        type="checkbox"
                                        checked={editForm.grade === 'C'}
                                        onChange={(e) => setGrade('C', (e.target as HTMLInputElement).checked)}
                                        disabled={fieldsDisabled || !!editForm.scrap}
                                    />
                                    <span>C</span>
                                </label>
                                <label className="gw-check-inline">
                                    <input
                                        type="checkbox"
                                        checked={editForm.grade === 'D'}
                                        onChange={(e) => setGrade('D', (e.target as HTMLInputElement).checked)}
                                        disabled={fieldsDisabled || !!editForm.scrap}
                                    />
                                    <span>D</span>
                                </label>
                                <label className="gw-check-inline">
                                    <input
                                        type="checkbox"
                                        checked={editForm.grade === 'E'}
                                        onChange={(e) => setGrade('E', (e.target as HTMLInputElement).checked)}
                                        disabled={fieldsDisabled || !editForm.scrap}
                                    />
                                    <span>E</span>
                                </label>

                            </div>
                        </fieldset>
                    </div>

                    {editForm.resold && (
                        <>
                            <div className="gw-form-row-compact" style={{ gap: 12 }}>
                                <label className="gw-form-field" style={{ flex: "1 1 50%" }}>
                                    <div className="gw-form-label">Försäljningsbatch</div>
                                    <select
                                        className="gw-input"
                                        value={editForm.salesBatchId || ""}
                                        onChange={(e) => handleSalesBatchChange(e.target.value)}
                                        disabled={fieldsDisabled}
                                    >
                                        <option value="">Välj batch</option>
                                        {salesBatchOpts.map((b) => (
                                            <option key={b.id} value={b.id}>{b.name || "(namnlös batch)"}</option>
                                        ))}
                                    </select>
                                </label>

                                <label className="gw-form-field" style={{ flex: "1 1 50%" }}>
                                    <div className="gw-form-label">Kolli</div>
                                    <select
                                        className="gw-input"
                                        value={editForm.salesKolliId || ""}
                                        onChange={(e) => handleSalesKolliChange(e.target.value)}
                                        disabled={fieldsDisabled || !(editForm.salesBatchId)}
                                    >
                                        <option value="">Välj kolli</option>
                                        {salesKolliOpts.map((k) => (
                                            <option key={k.id} value={k.id}>{k.name || "(namnlöst kolli)"}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            <div className="gw-form-row-compact" style={{ gap: 12 }}>
                                <label className="gw-form-field" style={{ flex: "0 1 33%" }}>
                                    <div className="gw-form-label">CPU</div>
                                    <input
                                        type="text"
                                        className="gw-input"
                                        value={editForm.salesCpu || ""}
                                        onChange={(e) => handleResaleField("salesCpu", e.target.value)}
                                        disabled={fieldsDisabled}
                                        placeholder="t.ex. i5-8365U"
                                    />
                                </label>
                                <label className="gw-form-field" style={{ flex: "0 1 33%" }}>
                                    <div className="gw-form-label">Lagring</div>
                                    <input
                                        type="text"
                                        className="gw-input"
                                        value={editForm.salesStorage || ""}
                                        onChange={(e) => handleResaleField("salesStorage", e.target.value)}
                                        disabled={fieldsDisabled}
                                        placeholder="t.ex. 512GB SSD"
                                    />
                                </label>

                                <label className="gw-form-field" style={{ flex: "0 1 33%" }}>
                                    <div className="gw-form-label">Minne / RAM</div>
                                    <input
                                        type="text"
                                        className="gw-input"
                                        value={editForm.salesRam || ""}
                                        onChange={(e) => handleResaleField("salesRam", e.target.value)}
                                        disabled={fieldsDisabled}
                                        placeholder="t.ex. 16GB"
                                    />
                                </label>
                            </div>

                        </>
                    )}

                    {(() => {
                        const allTypes = Object.keys(PHOTO_LABELS || {}) as string[];
                        if (!allTypes.length) return null;

                        return (
                            <div style={{ marginTop: 4 }}>
                                <h4 style={{ margin: "4px 0 6px" }}>Bilder</h4>

                                {allTypes.map((type) => (
                                    <input
                                        key={`file-${type}`}
                                        ref={(el) => { photoInputRefs.current[type] = el; }}
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const f = e.currentTarget.files?.[0];
                                            if (f) handleEditPhotoFile(type, f);
                                            e.currentTarget.value = "";
                                        }}
                                    />
                                ))}

                                <div className="gw-photo-grid">
                                    {allTypes.map((type) => {
                                        const label = (PHOTO_LABELS as any)[type] || type;

                                        const url = getPhotoSrcForUI(type);
                                        const isPendingAdd = !!pendingPhotoFiles[type];
                                        const isPendingDelete = !!pendingDeletes[type];
                                        const key = type as PhotoKey;
                                        const showImg = !!url && imgOk[key] !== false;

                                        if (showImg) {
                                            return (
                                                <div
                                                    key={type}
                                                    className="gw-photo-card"
                                                    onClick={() => setLargeImage(url!)}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => { if (e.key === "Enter") setLargeImage(url!); }}
                                                    title={`Visa ${label} i stort format`}
                                                >
                                                    {!fieldsDisabled && (
                                                        <button
                                                            type="button"
                                                            className="gw-photo-remove"
                                                            aria-label={`Ta bort ${label}`}
                                                            onClick={(e) => { e.stopPropagation(); removePhotoForType(type); }}
                                                            title="Ta bort"
                                                        >
                                                            ×
                                                        </button>
                                                    )}

                                                    <img
                                                        src={url!}
                                                        alt={label}
                                                        className="gw-photo-img"
                                                        onError={() => setImgOk(s => ({ ...s, [key]: false }))}
                                                    />

                                                    {isPendingAdd && (
                                                        <div style={{
                                                            position: "absolute", top: 6, left: 6, padding: "2px 8px",
                                                            borderRadius: 999, background: "rgba(17,24,39,0.85)",
                                                            color: "#fff", fontSize: 12
                                                        }}>
                                                            Ej sparat
                                                        </div>
                                                    )}

                                                    <div className="gw-photo-title">{label}</div>
                                                </div>
                                            );
                                        }

                                        return (
                                            <button
                                                key={type}
                                                type="button"
                                                className={`gw-photo-card ${fieldsDisabled ? "is-disabled" : ""}`}
                                                onClick={() => isPendingDelete ? restorePhotoForType(type) : triggerPhotoCapture(type)}
                                                title={isPendingDelete ? `Ångra borttagning av ${label}` : `Lägg till ${label}`}
                                                disabled={fieldsDisabled}
                                            >
                                                <div className="gw-photo-placeholder">
                                                    <div>
                                                        {isPendingDelete ? "Raderas vid spara – tryck för att ångra" : (fieldsDisabled ? "Låst läge" : "Tryck för att fota/ladda upp")}
                                                        <br />
                                                        <strong>{label}</strong>
                                                    </div>
                                                </div>
                                                <div className="gw-photo-title">{label}</div>
                                            </button>
                                        );
                                    })}
                                </div>

                                {largeImage && (
                                    <div
                                        onClick={() => setLargeImage(null)}
                                        style={{
                                            position: "fixed",
                                            inset: 0,
                                            background: "rgba(0,0,0,0.6)",
                                            display: "grid",
                                            placeItems: "center",
                                            zIndex: 9999,
                                            padding: 20,
                                        }}
                                    >
                                        <img
                                            src={largeImage}
                                            alt="Förhandsvisning"
                                            style={{
                                                maxWidth: "95vw",
                                                maxHeight: "90vh",
                                                borderRadius: 12,
                                                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    <section className="gw-form-field gw-form-row-full gw-log-section">
                        <div className="gw-form-label">Logg (senaste 3)</div>

                        <div
                            className="gw-log gw-logbox gw-mb-16 gw-log-scroll cursor-pointer"
                            onClick={() => setShowLogModal(true)}
                            title="Klicka för att visa full logg"
                        >
                            {limitedLog.length === 0 ? (
                                <div style={{ color: "#6b7280" }}>Ingen historik ännu.</div>
                            ) : (
                                (limitedLog as AuditEntry[]).map((entry, idx) => (
                                    <div key={idx} style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                                            {new Date(entry.at).toLocaleString("sv-SE")}
                                        </div>
                                        <div>
                                            <strong>{labelForAction(entry.action as string)}</strong>{" "}
                                            {entry.by || "—"}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </section>

                </div>

                <div className="gw-modal-footer">
                    <div>
                        {invoiceReportId && (
                            <button
                                type="button"
                                onClick={() => {
                                    if (isDirty) {
                                        const ok = window.confirm("Du har osparade ändringar. Vill du kasta dem?");
                                        if (!ok) return;

                                        Object.values(previewUrls).forEach((u) => {
                                            if (u?.startsWith?.("blob:")) { try { URL.revokeObjectURL(u); } catch { /* ignorera, städning är bäst-effort */ } }
                                        });
                                        setPreviewUrls({});
                                        setPendingPhotoFilesSynced({});
                                        setPendingDeletesSynced({});
                                        setIsDirty(false);
                                    }

                                    onClose();
                                    window.location.hash = `#/rapport/${encodeURIComponent(invoiceReportId!)}`;
                                }}
                                className="btn"
                                title="Öppna fakturarapport"
                                disabled={isSaving}
                            >
                                Öppna fakturarapport →
                            </button>
                        )}

                        {isDirty && (
                            <span
                                style={{
                                    marginLeft: 8,
                                    padding: "2px 8px",
                                    borderRadius: 999,
                                    background: "rgba(17,24,39,0.08)",
                                    color: "#111827",
                                    fontSize: 12,
                                }}
                            >
                                Ej sparade ändringar
                            </span>
                        )}
                    </div>

                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {isReadOnly ? (
                            <button type="button" className="btn" onClick={requestClose} disabled={isSaving}>
                                Stäng
                            </button>
                        ) : isCompleted ? (
                            <>
                                {onReopen && (
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={onReopen}
                                        disabled={isSaving}
                                    >
                                        Öppna för editering
                                    </button>
                                )}
                                <button type="button" className="btn" onClick={requestClose} disabled={isSaving}>
                                    Avbryt
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="btn"
                                    onClick={handleCompleteValidated}
                                    disabled={fieldsDisabled || !formComplete || isSaving || isDirty}
                                    title={
                                        isDirty
                                            ? "Spara eller återställ ändringarna först"
                                            : formComplete
                                            ? "Markera som färdig"
                                            : "Fyll i tillverkare, modell, serienummer och välj exakt ett alternativ"
                                    }
                                >
                                    {isSaving ? "Sparar…" : "Markera som färdig"}
                                </button>


                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={onSaveOrchestrated}
                                    disabled={fieldsDisabled || isSaving || !isDirty}
                                    title={
                                        isSaving
                                            ? "Sparar…"
                                            : isDirty
                                                ? "Spara ändringar"
                                                : "Inga ändringar att spara"
                                    }
                                >
                                    {isSaving ? "Sparar…" : "Spara ändringar"}
                                </button>

                                <button type="button" className="btn" onClick={requestClose} disabled={isSaving}>
                                    Avbryt
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
}

export default EditModal;
